import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatRelative } from '../lib/format';
import { useNavLayout } from '../lib/layout';
import { useTheme } from '../lib/theme';
import { BrandMark } from './Brand';
import { EmergencyAlert } from './EmergencyAlert';
import { ServiceUnavailablePage, useUnreachable } from '../../../shared/ErrorPage';
import { PasswordConfirmHost } from './PasswordConfirmHost';
import { RollensichtBanner, RollensichtSwitch } from './RollensichtSwitch';
import { Avatar, Dropdown } from './ui';
import { VersionInfo } from './VersionInfo';
import {
  IconBell,
  IconBoard,
  IconBox,
  IconBuilding,
  IconCalendar,
  IconCart,
  IconChart,
  IconChevronDown,
  IconClock,
  IconDashboard,
  IconFile,
  IconFlash,
  IconLayoutSidebar,
  IconLayoutTop,
  IconLink,
  IconLogout,
  IconMail,
  IconMenu,
  IconMonitor,
  IconMoon,
  IconPlus,
  IconRoute,
  IconSettings,
  IconShield,
  IconSpark,
  IconSun,
  IconTasks,
  IconTrash,
  IconTruck,
  IconUsers,
  IconWrench,
} from './icons';

interface NavEntry {
  to: string;
  label: string;
  icon: JSX.Element;
  permissions?: string[];
  end?: boolean;
}

/** Steht außerhalb jeder Gruppe – der ständige Einstiegspunkt. */
const NAV_TOP: NavEntry[] = [
  { to: '/', label: 'Dashboard', icon: <IconDashboard />, permissions: ['reports.dashboard'], end: true },
];

const NAV_SECTIONS: { title: string; items: NavEntry[] }[] = [
  {
    title: 'Betrieb',
    items: [
      { to: '/dispatch', label: 'Disposition', icon: <IconBoard />, permissions: ['dispatch.board', 'dispatch.assign'] },
      { to: '/eingang', label: 'Eingang', icon: <IconMail />, permissions: ['dispatch.board'] },
      { to: '/tasks', label: 'Auftragsübersicht', icon: <IconTasks />, permissions: ['tasks.read'] },
      { to: '/routes', label: 'Tourenplanung', icon: <IconRoute />, permissions: ['dispatch.route'] },
    ],
  },
  {
    title: 'Objekte',
    items: [
      { to: '/maintenance', label: 'Wartungspläne', icon: <IconCalendar />, permissions: ['maintenance.read'] },
      { to: '/meter-readings', label: 'Verbrauchsdatenerfassung', icon: <IconFlash />, permissions: ['properties.readings'] },
    ],
  },
  {
    title: 'Ressourcen',
    items: [
      { to: '/material', label: 'Material & Lagerwirtschaft', icon: <IconBox />, permissions: ['material.read'] },
      { to: '/invoices', label: 'Eingangsrechnungen', icon: <IconFile />, permissions: ['material.read'] },
      { to: '/time', label: 'Zeiterfassung', icon: <IconClock />, permissions: ['time.read'] },
      { to: '/vehicles', label: 'Fahrzeuge', icon: <IconTruck />, permissions: ['fleet.read'] },
    ],
  },
  {
    title: 'Anforderungen',
    items: [
      { to: '/procurement', label: 'Materialanforderungen', icon: <IconCart />, permissions: ['procurement.read'] },
      { to: '/contracting', label: 'Auftragsvergabe', icon: <IconUsers />, permissions: ['contracting.read'] },
    ],
  },
  {
    title: 'Geschäftspartner',
    items: [
      { to: '/contractors', label: 'Dienstleister', icon: <IconUsers />, permissions: ['contracting.read'] },
      { to: '/utility-suppliers', label: 'Versorger', icon: <IconFlash />, permissions: ['utilities.read'] },
      { to: '/utility-tariffs', label: 'Versorgungstarife', icon: <IconFile />, permissions: ['utilities.read'] },
    ],
  },
  {
    title: 'Analyse',
    items: [
      { to: '/reports', label: 'Auswertungen', icon: <IconChart />, permissions: ['reports.operational', 'reports.financial'] },
    ],
  },
  {
    title: 'Stammdaten',
    items: [
      { to: '/properties', label: 'Objektdaten', icon: <IconBuilding />, permissions: ['properties.read'] },
      { to: '/customers', label: 'Eigentümer/Verwalter', icon: <IconUsers />, permissions: ['customers.read'] },
      { to: '/warehouses', label: 'Lager', icon: <IconBox />, permissions: ['material.read'] },
      { to: '/admin/catalogs', label: 'Servicekatalog', icon: <IconWrench />, permissions: ['admin.catalogs.read'] },
      { to: '/admin/calendar', label: 'Betriebskalender', icon: <IconCalendar />, permissions: ['admin.catalogs.read'] },
      { to: '/admin/tenants', label: 'Organisation', icon: <IconBuilding />, permissions: ['admin.tenants'] },
    ],
  },
  {
    title: 'Konfiguration',
    items: [
      { to: '/admin/settings', label: 'Systemeinstellungen', icon: <IconSettings />, permissions: ['admin.settings'] },
      { to: '/admin/channels', label: 'Meldekanäle', icon: <IconMail />, permissions: ['admin.settings'] },
      { to: '/admin/portal', label: 'Portalseite', icon: <IconMonitor />, permissions: ['admin.settings'] },
      { to: '/admin/ai-settings', label: 'KI-Einstellungen', icon: <IconSpark />, permissions: ['admin.ai'] },
      { to: '/admin/qr', label: 'QR-Codes', icon: <IconLink />, permissions: ['properties.update'] },
      { to: '/admin/push', label: 'Push-Mitteilungen', icon: <IconBell />, permissions: ['admin.settings'] },
      { to: '/admin/notifications', label: 'Benachrichtigungsmanager', icon: <IconMail />, permissions: ['admin.settings'] },
      { to: '/admin/documents', label: 'Dokumentengenerator', icon: <IconFile />, permissions: ['admin.settings'] },
    ],
  },
  // Die Administration zerfällt in zwei Fragen, die verschiedene Personen
  // stellen: „Wer darf was?“ beantwortet die Personalverwaltung, „Wie
  // arbeitet der Betrieb?“ die fachliche Leitung. Ein gemeinsamer Block
  // „Administration“ zwang beide, jedes Mal die ganze Liste zu lesen.
  {
    title: 'Zugang & Rechteverwaltung',
    items: [
      { to: '/admin/users', label: 'Benutzer', icon: <IconUsers />, permissions: ['admin.users.read'] },
      { to: '/admin/groups', label: 'Gruppen', icon: <IconUsers />, permissions: ['admin.groups.read'] },
      { to: '/admin/roles', label: 'Rollen & Berechtigungen', icon: <IconShield />, permissions: ['admin.roles.read'] },
    ],
  },
  // Eigene Gruppe, obwohl heute nur ein Eintrag darin steht: Der Nachweis ist
  // keine Konfiguration und gehört nicht zwischen die Einstellungsseiten. Die
  // Gruppe ist als Platz für die weiteren Auswertungen angelegt, die hier
  // dazukommen sollen – bis dahin kostet sie einen Klick, den ein einzelner
  // Eintrag am Fuß der Leiste nicht gekostet hat.
  {
    title: 'Audit',
    items: [
      { to: '/admin/audit', label: 'Audit-Log', icon: <IconShield />, permissions: ['admin.audit'] },
      { to: '/admin/dsfa', label: 'DSFA-Report', icon: <IconShield />, permissions: ['admin.settings'] },
      { to: '/admin/retention', label: 'Löschfristen', icon: <IconTrash />, permissions: ['admin.settings'] },
    ],
  },
];

const NAV_GROUPS_KEY = 'ff.navGroups';

/**
 * Aufgeklappte Menügruppen – im Browser gespeichert wie die Layoutwahl auch.
 *
 * Bewusst *keine* Ziehharmonika: Wer zwei Gruppen offen haben will, soll sie
 * offen behalten dürfen. Eine Gruppe schließt sich nur, wenn jemand sie selbst
 * zuklappt – auch ein Seitenwechsel ändert daran nichts.
 *
 * Beim allerersten Aufruf ist genau die Gruppe offen, in der das aktuelle Ziel
 * liegt. Alles zugeklappt zu zeigen wäre zwar am aufgeräumtesten, ließe aber
 * niemanden erkennen, dass hinter den Überschriften überhaupt etwas steckt.
 */
function readOpenGroups(pathname: string): string[] {
  const stored = localStorage.getItem(NAV_GROUPS_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed.filter((value) => typeof value === 'string');
    } catch {
      /* beschädigter Eintrag – dann eben der Erstzustand */
    }
  }
  const treffer = NAV_SECTIONS.find((section) =>
    section.items.some((item) => item.to !== '/' && pathname.startsWith(item.to)),
  );
  return treffer ? [treffer.title] : [];
}

export function Layout() {
  const { user, tenant, logout, canAny, isParked } = useAuth();
  const unreachable = useUnreachable();
  const { preference, resolved, setPreference } = useTheme();
  const { navLayout, setNavLayout } = useNavLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState<string[]>(() => readOpenGroups(location.pathname));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState<{ items: any[]; unread: number }>({ items: [], unread: 0 });

  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  const toggleGroup = (title: string) =>
    setOpenGroups((current) =>
      current.includes(title) ? current.filter((eintrag) => eintrag !== title) : [...current, title],
    );

  useEffect(() => {
    let active = true;
    const load = () =>
      api
        .get('/api/notifications')
        .then((data) => active && setNotifications(data))
        .catch(() => undefined);
    void load();
    const timer = setInterval(load, 60_000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  const markAllRead = async () => {
    await api.post('/api/notifications/read-all');
    setNotifications((current) => ({
      items: current.items.map((item) => ({ ...item, read_at: new Date().toISOString() })),
      unread: 0,
    }));
  };

  // Bei stillgelegtem Konto bleibt das Menü vollständig stehen, aber ohne
  // Ziel. Es nach Rechten zu filtern hieße hier: gar nichts anzuzeigen – ein
  // leerer Rahmen sieht nach einem Fehler aus, ein graues Menü nach einem
  // Zustand, den jemand erklären kann.
  const visibleSections = isParked
    ? NAV_SECTIONS
    : NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.permissions || canAny(...item.permissions)),
      })).filter((section) => section.items.length > 0);

  const filterEntries = (entries: NavEntry[]) =>
    isParked ? entries : entries.filter((item) => !item.permissions || canAny(...item.permissions));
  const visibleTop = filterEntries(NAV_TOP);

  // Stillgelegtes Konto: Die Einträge bleiben sichtbar, aber ohne Ziel. Sie
  // ganz auszublenden ließe die Person rätseln, ob ihr Zugang beschädigt
  // ist; so ist erkennbar, dass es die Bereiche gibt und der Zugriff bloß ruht.
  const renderSidebarItem = (item: NavEntry) =>
    isParked ? (
      <span key={item.to} className="nav-item disabled" aria-disabled="true" title="Ohne Mandantenzuordnung nicht verfügbar">
        <span className="icon">{item.icon}</span>
        <span>{item.label}</span>
      </span>
    ) : (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
        title={item.label}
      >
        <span className="icon">{item.icon}</span>
        <span>{item.label}</span>
      </NavLink>
    );

  const sidebarMode = navLayout === 'sidebar';

  return (
    <div className={`app-shell ${sidebarMode ? '' : 'nav-top'} ${collapsed && sidebarMode ? 'collapsed' : ''}`}>
      {/* Markenband: greift den Farbverlauf des Logos auf. */}
      <div className="brand-bar" aria-hidden="true" />

      {/* Notfallmeldung – legt sich über alles, verlangt eine Bestätigung. */}
      <EmergencyAlert />

      {/* Testsicht auf eine Rolle – dauerhaft sichtbar, solange sie läuft. */}
      <RollensichtBanner />
      <PasswordConfirmHost />

      {sidebarMode && (
        <aside className={`sidebar ${mobileOpen ? 'open' : ''}`}>
          <div className="sidebar-brand">
            <div className="sidebar-brand-top">
              <BrandMark size={56} />
              <strong className="sidebar-wordmark">FacilityFlow</strong>
            </div>
            <span className="sidebar-tenant">{tenant?.name ?? 'Immobilienbetreuung'}</span>
          </div>
          <nav className="sidebar-nav">
            {visibleTop.map(renderSidebarItem)}
            {visibleSections.map((section) => {
              // Im schmalen Icon-Streifen ist die Gruppenüberschrift
              // ausgeblendet – eine zugeklappte Gruppe ließe sich dort nicht
              // wieder öffnen. Das Ein- und Ausklappen gilt deshalb nur im
              // breiten Menü; im Streifen stehen immer alle Einträge.
              const offen = collapsed || openGroups.includes(section.title);
              return (
                <div key={section.title} className="nav-section">
                  <button
                    type="button"
                    className={`nav-section-title ${offen ? 'open' : ''}`}
                    onClick={() => toggleGroup(section.title)}
                    aria-expanded={offen}
                  >
                    <span>{section.title}</span>
                    <IconChevronDown width={12} height={12} />
                  </button>
                  {offen && section.items.map(renderSidebarItem)}
                </div>
              );
            })}
          </nav>
          <div className="sidebar-footer">
            <VersionInfo />
          </div>
        </aside>
      )}

      <header className="topbar">
        {sidebarMode ? (
          <button
            className="btn ghost icon-only"
            onClick={() => {
              if (window.innerWidth <= 820) setMobileOpen((value) => !value);
              else setCollapsed((value) => !value);
            }}
            aria-label="Navigation umschalten"
          >
            <IconMenu />
          </button>
        ) : (
          <div className="topbar-brand">
            <BrandMark size={34} />
            <strong>FacilityFlow</strong>
            <VersionInfo compact />
          </div>
        )}

        <div className="topbar-title">
          <h1>{titleFor(location.pathname)}</h1>
          {!sidebarMode && tenant?.name && <p>{tenant.name}</p>}
        </div>


        {/* Schnellzugriffe – die Wege, die im Betrieb am häufigsten fallen.
            Bei stillgelegtem Konto führt keiner davon irgendwohin. */}
        <div className="quick-actions" style={isParked ? { display: 'none' } : undefined}>
          {canAny('dispatch.route') && (
            <button className="btn soft sm" onClick={() => navigate('/routes')} title="Tourenplanung">
              <IconRoute width={15} height={15} />
              <span>Tour</span>
            </button>
          )}
          {canAny('tasks.create') && (
            <button className="btn primary sm" onClick={() => navigate('/tasks/new')} title="Neue Meldung erfassen">
              <IconPlus width={15} height={15} />
              <span>Neue Meldung</span>
            </button>
          )}
        </div>

        <Dropdown
          trigger={() => (
            <button className="btn ghost icon-only" style={{ position: 'relative' }} aria-label="Benachrichtigungen">
              <IconBell />
              {notifications.unread > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: 3,
                    right: 3,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: 'var(--danger)',
                  }}
                />
              )}
            </button>
          )}
        >
          <div className="row between" style={{ padding: '4px 8px' }}>
            <span className="dropdown-label" style={{ padding: 0 }}>
              Benachrichtigungen
            </span>
            {notifications.unread > 0 && (
              <button className="btn ghost sm" onClick={markAllRead}>
                Alle gelesen
              </button>
            )}
          </div>
          <div className="dropdown-divider" />
          {notifications.items.length === 0 && (
            <div className="small subtle" style={{ padding: 12 }}>
              Keine Benachrichtigungen.
            </div>
          )}
          {notifications.items.slice(0, 8).map((item) => (
            <div
              key={item.id}
              className={`notification-item ${item.read_at ? '' : 'unread'}`}
              onClick={() => {
                if (item.task_id) navigate(`/tasks/${item.task_id}`);
                else if (typeof item.link === 'string' && item.link.startsWith('/')) navigate(item.link);
              }}
              style={{ cursor: item.task_id || item.link ? 'pointer' : 'default' }}
            >
              <strong style={{ fontSize: 12.5 }}>{item.title}</strong>
              {item.body && <div className="small muted truncate">{item.body}</div>}
              <div className="meta">{formatRelative(item.created_at)}</div>
            </div>
          ))}
        </Dropdown>

        <Dropdown
          menuClassName="user-menu"
          trigger={() => (
            <button
              className="btn ghost"
              style={{ gap: 8, ...(user?.rollensicht ? { borderColor: 'var(--warning)', background: 'var(--warning-soft)' } : {}) }}
              title={user?.rollensicht ? `Testsicht aktiv: ${user.rollensicht.name}` : undefined}
            >
              <Avatar firstName={user?.firstName} lastName={user?.lastName} size="sm" />
              <span className="nowrap topbar-user-name">
                {user?.firstName} {user?.lastName}
              </span>
            </button>
          )}
        >
          <div style={{ padding: '6px 10px' }}>
            <strong style={{ fontSize: 13 }}>
              {user?.firstName} {user?.lastName}
            </strong>
            <div className="small subtle">{user?.email}</div>
            <div className="row" style={{ gap: 4, marginTop: 6 }}>
              {user?.isSuperadmin && <span className="badge danger">Superadmin</span>}
              {user?.roles.slice(0, 2).map((role) => (
                <span className="badge" key={role.id}>
                  {role.name}
                </span>
              ))}
            </div>
          </div>
          <RollensichtSwitch />
          {/* Darstellung: Anordnung der Navigation und Hell/Dunkel/System. Das Menü
              schließt bei jedem Klick in sich; hier soll es offen bleiben, damit
              man die Wirkung sieht. Die Anordnung entfällt auf dem Telefon – dort
              ist die Seitenleiste ohnehin eine Schublade. */}
          <div className="dropdown-divider" />
          <div style={{ padding: '6px 10px' }} onClick={(event) => event.stopPropagation()}>
            <div className="topbar-layout-switch" style={{ marginBottom: 10 }}>
              <div className="small subtle" style={{ marginBottom: 4 }}>Navigation</div>
              <div className="btn-group menu-group" role="group" aria-label="Navigation anordnen">
                <button
                  className={`btn sm ${sidebarMode ? 'active' : ''}`}
                  onClick={() => setNavLayout('sidebar')}
                  title="Menü als Leiste am linken Rand"
                  aria-pressed={sidebarMode}
                >
                  <IconLayoutSidebar width={15} height={15} /> Leiste
                </button>
                <button
                  className={`btn sm ${sidebarMode ? '' : 'active'}`}
                  onClick={() => setNavLayout('topbar')}
                  title="Menü als aufklappbare Menüzeile"
                  aria-pressed={!sidebarMode}
                >
                  <IconLayoutTop width={15} height={15} /> Menüzeile
                </button>
              </div>
            </div>
            <div className="small subtle" style={{ marginBottom: 4 }}>Erscheinungsbild</div>
            <div className="btn-group menu-group" role="group" aria-label="Erscheinungsbild">
              <button className={`btn sm ${preference === 'light' ? 'active' : ''}`} onClick={() => setPreference('light')} title="Helles Design">
                <IconSun width={15} height={15} /> Hell
              </button>
              <button className={`btn sm ${preference === 'dark' ? 'active' : ''}`} onClick={() => setPreference('dark')} title="Dunkles Design">
                <IconMoon width={15} height={15} /> Dunkel
              </button>
              <button
                className={`btn sm ${preference === 'system' ? 'active' : ''}`}
                onClick={() => setPreference('system')}
                title={`Systemeinstellung (aktuell ${resolved === 'dark' ? 'dunkel' : 'hell'})`}
              >
                <IconMonitor width={15} height={15} /> System
              </button>
            </div>
          </div>
          <div className="dropdown-divider" />
          <button className="dropdown-item" onClick={() => navigate('/profile')}>
            <IconSettings width={15} height={15} /> Profil & Sicherheit
          </button>
          <button className="dropdown-item" onClick={() => navigate('/profile#permissions')}>
            <IconShield width={15} height={15} /> Meine Berechtigungen
          </button>
          <div className="dropdown-divider" />
          <button
            className="dropdown-item danger"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
          >
            <IconLogout width={15} height={15} /> Abmelden
          </button>
        </Dropdown>
      </header>

      {!sidebarMode && (
        <nav className="menubar" aria-label="Hauptnavigation">
          {visibleTop.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `menubar-item ${isActive ? 'active' : ''}`}
            >
              {item.label}
            </NavLink>
          ))}
          {visibleSections.map((section) => {
            const containsCurrent = section.items.some((item) =>
              item.end ? location.pathname === item.to : location.pathname.startsWith(item.to),
            );
            return (
              <Dropdown
                key={section.title}
                trigger={(open) => (
                  <button className={`menubar-item ${containsCurrent ? 'active' : ''} ${open ? 'open' : ''}`}>
                    {section.title}
                    <IconChevronDown width={13} height={13} />
                  </button>
                )}
              >
                {section.items.map((item) => (
                  <button key={item.to} className="dropdown-item" onClick={() => navigate(item.to)}>
                    <span className="icon">{item.icon}</span>
                    {item.label}
                  </button>
                ))}
              </Dropdown>
            );
          })}
        </nav>
      )}

      <main className="main">
        {unreachable ? <ServiceUnavailablePage /> : <Outlet />}
      </main>
    </div>
  );
}

function titleFor(pathname: string): string {
  const map: Record<string, string> = {
    '/': 'Dashboard',
    '/tasks': 'Aufträge',
    '/tasks/new': 'Neue Meldung',
    '/dispatch': 'Disposition',
    '/eingang': 'Eingang',
    '/routes': 'Tourenplanung',
    '/properties': 'Objektdaten & Liegenschaften',
    '/customers': 'Eigentümer/Verwalter',
    '/warehouses': 'Lager',
    '/material': 'Material & Lagerwirtschaft',
    '/invoices': 'Eingangsrechnungen',
    '/vehicles': 'Fuhrpark',
    '/procurement': 'Materialanforderungen',
    '/contracting': 'Auftragsvergabe',
    '/contractors': 'Dienstleister',
    '/utility-suppliers': 'Versorger',
    '/utility-tariffs': 'Versorgungstarife',
    '/time': 'Zeiterfassung',
    '/reports': 'Auswertungen',
    '/maintenance': 'Wartungspläne',
    '/meter-readings': 'Verbrauchsdatenerfassung',
    '/profile': 'Profil',
    '/admin/users': 'Benutzerverwaltung',
    '/admin/groups': 'Gruppenverwaltung',
    '/admin/roles': 'Rollen & Berechtigungen',
    '/admin/catalogs': 'Servicekatalog',
    '/admin/tenants': 'Organisation',
    '/admin/audit': 'Audit-Log',
    '/admin/dsfa': 'DSFA-Report',
    '/admin/retention': 'Löschfristen',
    '/admin/settings': 'Systemeinstellungen',
    '/admin/ai-settings': 'KI-Einstellungen',
    '/admin/channels': 'Meldekanäle',
    '/admin/portal': 'Portalseite',
    '/admin/qr': 'QR-Codes',
    '/admin/calendar': 'Betriebskalender',
    '/admin/push': 'Push-Mitteilungen',
    '/admin/notifications': 'Benachrichtigungsmanager',
    '/admin/documents': 'Dokumentengenerator',
  };
  if (map[pathname]) return map[pathname];
  if (pathname.startsWith('/tasks/')) return 'Auftragsdetail';
  if (pathname.startsWith('/properties/')) return 'Objektdetail';
  if (pathname.startsWith('/admin/users/')) return 'Benutzerdetail';
  return 'FacilityFlow';
}
